/**
 * ShareFolderDialog — Per-folder ACL management UI (Wave 2 finish, b51).
 *
 * Mirrors ShareDocumentDialog but anchored to a folder. v1 does NOT
 * recurse into sub-folders — a folder grant applies only to docs
 * directly in this folder. Folder grants get an "inherited from folder"
 * badge on the per-doc share dialog when they apply to a doc.
 */

import { useState, useMemo } from 'react';
import { Loader2, FolderOpen, X, Trash2, Info, Plus } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { apiClient } from '@/lib/api-client';
import { getApiErrorMessage } from '@/lib/api-error';
import { toast } from 'sonner';
import { useDepartments } from '@/lib/use-users';
import { ShareRolePicker, useShareRoleTab } from './share-role-picker';
import { cn } from '@/lib/utils';
// THE display rule for a person (LB6-T4-BF-05): the picker and the
// current-access list name people as every Library tab does.
import { userPickerLabel, type NamedUser } from '@argusai/shared/schemas/user-display';

// ─── Types (mirror routes/v1/library/acl.ts) ───────────────────────────────

// 'department' added by #176 / bAUTO_1101.
type AclSubjectKind = 'user' | 'group' | 'role' | 'department';
type AclPermission = 'view' | 'edit' | 'delete' | 'share';

interface AclEntry {
  grantId: string;
  subjectKind: AclSubjectKind;
  subjectUserId: string | null;
  subjectGroupId: string | null;
  subjectRole: string | null;
  subjectDepartmentId: string | null;
  permission: AclPermission;
  grantedBy: string | null;
  grantedAt: string;
  expiresAt: string | null;
  reason: string | null;
  source: 'direct' | 'inherited_folder';
  /** A `user` share's subject, named by the server (LB6-T4-R5); null otherwise. */
  subjectUser: NamedUser | null;
}

interface EffectiveAclResponse {
  direct: AclEntry[];
  inheritedFromFolder: AclEntry[];
  effective: AclEntry[];
}

interface UserListItem {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

interface GroupListItem {
  id: string;
  name: string;
}

interface ShareFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folderId: string;
  folderName?: string;
}

const PERMISSIONS: Array<{ value: AclPermission; label: string }> = [
  { value: 'view', label: 'View' },
  { value: 'edit', label: 'Edit' },
  { value: 'delete', label: 'Delete' },
  { value: 'share', label: 'Share' },
];

// The Role tab's options are the tenant's roles from the Users rail —
// shipped and custom (decision 27) — read by ShareRolePicker (L6-05).

// ─── Component ─────────────────────────────────────────────────────────────

export function ShareFolderDialog({
  open,
  onOpenChange,
  folderId,
  folderName,
}: ShareFolderDialogProps) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<AclSubjectKind>('user');
  // The Role tab only for a person who may read its options (review BF-07).
  const roleTab = useShareRoleTab();

  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<string>('');
  // Department subject (#176).
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>('');
  const [permission, setPermission] = useState<AclPermission>('view');
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [userSearch, setUserSearch] = useState<string>('');

  const aclQueryKey = useMemo(
    () => ['library', 'acl', 'folder', folderId],
    [folderId],
  );

  const aclQuery = useQuery({
    queryKey: aclQueryKey,
    queryFn: () => apiClient.get<EffectiveAclResponse>(`/library/acl/folders/${folderId}`),
    enabled: open,
  });

  // `/users/search`, not `/users` — see the note in ShareDocumentDialog.tsx
  // (review R-01 / BF-07, B1-T2b). The roster route is the admin audience's;
  // a Member with `share:library` 403'd here and the Users tab never loaded.
  const usersQuery = useQuery({
    queryKey: ['users', 'search', { search: userSearch, scope: 'share-folder' }],
    queryFn: () => apiClient.get<{ data: UserListItem[] }>(
      `/users/search?pageSize=50${userSearch ? `&search=${encodeURIComponent(userSearch)}` : ''}`,
    ),
    enabled: open && activeTab === 'user',
  });

  const groupsQuery = useQuery({
    queryKey: ['users', 'groups'],
    queryFn: () => apiClient.get<{ data: GroupListItem[] }>('/users/groups?pageSize=200'),
    enabled: open && activeTab === 'group',
  });

  // #176 — tenant departments for the new tab.
  const { departments: tenantDepartments, isLoading: deptsLoading } = useDepartments({
    pageSize: 200,
  });

  const addGrant = useMutation({
    mutationFn: async (body: {
      subjectKind: AclSubjectKind;
      subjectUserId?: string | null;
      subjectGroupId?: string | null;
      subjectRole?: string | null;
      subjectDepartmentId?: string | null;
      permission: AclPermission;
      expiresAt?: string | null;
      reason?: string | null;
    }) => {
      return apiClient.post<{ grantId: string; created: boolean }>(
        `/library/acl/folders/${folderId}/grants`,
        body,
      );
    },
    onSuccess: (result) => {
      if (result.created) toast.success('Folder grant added');
      else toast.info('Folder grant already existed (no change)');
      queryClient.invalidateQueries({ queryKey: aclQueryKey });
      setSelectedUserId('');
      setSelectedGroupId('');
      setSelectedRole('');
      setSelectedDepartmentId('');
      setPermission('view');
      setExpiresAt('');
      setReason('');
    },
    onError: (err: Error & { data?: { error?: { message?: string } } }) => {
      toast.error(getApiErrorMessage(err, 'Failed to add folder grant'));
    },
  });

  const removeGrant = useMutation({
    mutationFn: async (grantId: string) => {
      return apiClient.delete(`/library/acl/folders/${folderId}/grants/${grantId}`);
    },
    onSuccess: () => {
      toast.success('Folder grant removed');
      queryClient.invalidateQueries({ queryKey: aclQueryKey });
    },
    onError: (err: Error & { data?: { error?: { message?: string } } }) => {
      toast.error(getApiErrorMessage(err, 'Failed to remove folder grant'));
    },
  });

  const handleAddGrant = () => {
    if (activeTab === 'user') {
      if (!selectedUserId) { toast.error('Pick a user first'); return; }
      addGrant.mutate({
        subjectKind: 'user',
        subjectUserId: selectedUserId,
        permission,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        reason: reason.trim() || null,
      });
    } else if (activeTab === 'group') {
      if (!selectedGroupId) { toast.error('Pick a group first'); return; }
      addGrant.mutate({
        subjectKind: 'group',
        subjectGroupId: selectedGroupId,
        permission,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        reason: reason.trim() || null,
      });
    } else if (activeTab === 'role') {
      if (!selectedRole) { toast.error('Pick a role first'); return; }
      addGrant.mutate({
        subjectKind: 'role',
        subjectRole: selectedRole,
        permission,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        reason: reason.trim() || null,
      });
    } else {
      // department (#176)
      if (!selectedDepartmentId) { toast.error('Pick a department first'); return; }
      addGrant.mutate({
        subjectKind: 'department',
        subjectDepartmentId: selectedDepartmentId,
        permission,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        reason: reason.trim() || null,
      });
    }
  };

  function entryLabel(
    e: AclEntry,
    groups: GroupListItem[] | undefined,
    depts: Array<{ id: string; name: string }> | undefined,
  ): string {
    if (e.subjectKind === 'user') {
      // The server names the subject with the share (LB6-T4-R5) — never a
      // lookup in the picker's current page, which is not a directory. A
      // subject it does not name is an account outside this tenant.
      return e.subjectUser ? userPickerLabel(e.subjectUser) : 'Outside this tenant';
    }
    if (e.subjectKind === 'group') {
      const g = groups?.find((g) => g.id === e.subjectGroupId);
      return g ? g.name : e.subjectGroupId ?? 'Unknown group';
    }
    if (e.subjectKind === 'department') {
      const d = depts?.find((d) => d.id === e.subjectDepartmentId);
      return d ? `Department: ${d.name}` : `Department: ${e.subjectDepartmentId ?? 'unknown'}`;
    }
    return `Role: ${e.subjectRole ?? 'unknown'}`;
  }

  const directEntries = aclQuery.data?.direct ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="h-4 w-4" /> Share folder
          </DialogTitle>
          <DialogDescription>
            Grant view, edit, delete, or share access on{' '}
            <span className="font-medium">{folderName ?? 'this folder'}</span>.
            Folder grants apply to all docs directly in this folder unless
            overridden by a per-doc grant.
          </DialogDescription>
        </DialogHeader>

        {/* Decision 22 (B2): a share RESTRICTS — said plainly, before any
            grant is made (L6-05). It says what the resolver does
            (policy/document-visibility.ts sharesAllowSql), no more (review
            R3): a folder share closes the documents DIRECTLY in the folder;
            a document with a live share of its own follows its own shares,
            not the folder's; and the folder itself stays listed (the
            folder-name rule is clearance only). */}
        <p
          className="text-sm rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-amber-800 dark:text-amber-300"
          data-testid="share-restricts-notice"
        >
          <strong>Sharing restricts access.</strong> Once this folder has any share, the documents
          directly in it can be seen only by the people, groups, roles and departments it is shared
          with, and by each document's uploader. Everyone else loses access to them. A document
          that has shares of its own follows those instead, and the folder itself stays listed.
        </p>

        <div className="space-y-5 py-2">
          {/* #176 — Department-based ACL is now supported. Same model as
              the per-doc dialog. */}
          <div className="text-xs px-3 py-2 rounded-md bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/20">
            <strong>Access scope.</strong> Folder access is enforced by
            tenant + classification (sensitivity / export control) +
            grants you set below (user / group / role / department).
            Grants compose — a user gets access via any matching subject.
          </div>

          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as AclSubjectKind)}>
            <TabsList className={cn('w-full grid', roleTab ? 'grid-cols-4' : 'grid-cols-3')}>
              <TabsTrigger value="user">Direct (User)</TabsTrigger>
              <TabsTrigger value="group">Group</TabsTrigger>
              {roleTab && <TabsTrigger value="role">Role</TabsTrigger>}
              <TabsTrigger value="department">Department</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="space-y-3 border rounded-md p-3 bg-muted/20">
            {activeTab === 'user' && (
              <div className="space-y-2">
                <Label htmlFor="folder-user-search">User</Label>
                <Input
                  id="folder-user-search"
                  placeholder="Search users by name or email..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                />
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pick a user" />
                  </SelectTrigger>
                  <SelectContent>
                    {usersQuery.isLoading && (
                      <SelectItem value="__loading__" disabled>Loading...</SelectItem>
                    )}
                    {usersQuery.data?.data?.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {userPickerLabel(u)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {activeTab === 'group' && (
              <div className="space-y-2">
                <Label htmlFor="folder-group-pick">Group</Label>
                <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
                  <SelectTrigger id="folder-group-pick">
                    <SelectValue placeholder="Pick a group" />
                  </SelectTrigger>
                  <SelectContent>
                    {groupsQuery.isLoading && (
                      <SelectItem value="__loading__" disabled>Loading...</SelectItem>
                    )}
                    {groupsQuery.data?.data?.map((g) => (
                      <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {roleTab && activeTab === 'role' && (
              <ShareRolePicker id="folder-role-pick" value={selectedRole} onChange={setSelectedRole} />
            )}

            {activeTab === 'department' && (
              <div className="space-y-2">
                <Label htmlFor="folder-dept-pick">Department</Label>
                <Select value={selectedDepartmentId} onValueChange={setSelectedDepartmentId}>
                  <SelectTrigger id="folder-dept-pick">
                    <SelectValue placeholder="Pick a department" />
                  </SelectTrigger>
                  <SelectContent>
                    {deptsLoading && (
                      <SelectItem value="__loading__" disabled>Loading...</SelectItem>
                    )}
                    {!deptsLoading && tenantDepartments.length === 0 && (
                      <SelectItem value="__empty__" disabled>
                        No departments — create one in Users → Departments first
                      </SelectItem>
                    )}
                    {tenantDepartments.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Every user whose profile department matches will be granted
                  the selected permission on this folder.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="folder-perm-pick">Permission</Label>
                <Select value={permission} onValueChange={(v) => setPermission(v as AclPermission)}>
                  <SelectTrigger id="folder-perm-pick">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PERMISSIONS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="folder-expires-at">Expires at (optional)</Label>
                <Input
                  id="folder-expires-at"
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="folder-reason">Reason (optional)</Label>
              <Textarea
                id="folder-reason"
                placeholder="Why is this access being granted?"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
              />
            </div>

            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={handleAddGrant}
                disabled={addGrant.isPending}
              >
                {addGrant.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                ) : (
                  <Plus className="h-3.5 w-3.5 mr-2" />
                )}
                Add folder grant
              </Button>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium">Folder grants</h3>
              <span className="text-xs text-muted-foreground">
                {directEntries.length} grant{directEntries.length === 1 ? '' : 's'}
              </span>
            </div>
            {aclQuery.isLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading...
              </div>
            )}
            {aclQuery.isError && (() => {
              // Decision 18 — the sidebar and the folder ACL routes read the
              // ONE folder store, so every folder the sidebar shows can be
              // shared (L18-07). A 404 now means only that the folder is
              // gone (deleted since the sidebar was read).
              const err = aclQuery.error as { data?: { error?: { code?: string; message?: string } } } | undefined;
              const code = err?.data?.error?.code;
              if (code === 'NOT_FOUND') {
                return (
                  <div className="text-sm text-destructive py-4">
                    This folder no longer exists. Close this dialog and refresh the Library.
                  </div>
                );
              }
              return (
                <div className="text-sm text-destructive py-4">
                  Failed to load folder ACL: {getApiErrorMessage(err, 'Unknown error')}.
                </div>
              );
            })()}
            {!aclQuery.isLoading && !aclQuery.isError && directEntries.length === 0 && (
              <div className="text-sm text-muted-foreground py-4 flex items-center gap-2">
                <Info className="h-3.5 w-3.5" />
                No folder grants yet.
              </div>
            )}
            <ScrollArea className="max-h-64">
              <div className="space-y-1.5">
                {directEntries.map((e) => (
                  <div
                    key={e.grantId}
                    className="flex items-center justify-between gap-2 px-3 py-2 border rounded-md text-sm"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">
                        {entryLabel(e, groupsQuery.data?.data, tenantDepartments)}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Badge variant="outline" className="text-[10px]">
                          {e.permission}
                        </Badge>
                        {e.expiresAt && (
                          <Badge variant="outline" className="text-[10px]">
                            expires {new Date(e.expiresAt).toLocaleDateString()}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      title="Remove folder grant"
                      disabled={removeGrant.isPending}
                      onClick={() => {
                        if (confirm('Remove this folder grant?')) {
                          removeGrant.mutate(e.grantId);
                        }
                      }}
                    >
                      {removeGrant.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="h-3.5 w-3.5 mr-2" /> Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
